import type { AppInstancePayload } from '@wix/platform-editor-sdk';
import type { ExperimentsProps } from '@wix/wix-experiments';
import Experiments from '@wix/wix-experiments';

import {
  getIsBuilderEditor,
  getIsClassicEditor,
  getIsResponsiveEditor,
  getIsStudio2Editor,
} from '../editor-app-module/services/applicationState';

let conductedExperiments: Experiments | null = null;
let conductedV3Experiments: Experiments | null = null;

// Since we conduct without yoshi, we should still call petri in the same way they do
// Aligned to https://github.com/wix-private/yoshi/blob/master/packages/yoshi-flow-editor-runtime/src/experiments/createExperimentsFromEssentials.ts#L17
function toRequestContext({
  siteOwnerId,
  metaSiteId,
  uid: loggedInUserId,
}: AppInstancePayload): ExperimentsProps['requestContext'] {
  return {
    forSiteOwner: {
      loggedInUserId,
      siteOwnerId,
    },
    overrideCriteria: {
      entityId: metaSiteId,
    },
  };
}

async function initExperiments(appInstance: AppInstancePayload) {
  conductedExperiments = new Experiments({
    scope: 'members-area',
    requestContext: toRequestContext(appInstance),
    useNewApi: true,
  });
  await waitForConduction();
}

async function waitForConduction() {
  if (conductedExperiments) {
    await conductedExperiments.ready();
  }
}

async function initV3Experiments(appInstance: AppInstancePayload) {
  conductedV3Experiments = new Experiments({
    scope: 'members-area-v3',
    requestContext: toRequestContext(appInstance),
    useNewApi: true,
  });
  await waitForV3Conduction();
}

async function waitForV3Conduction() {
  if (conductedV3Experiments) {
    await conductedV3Experiments.ready();
  }
}

async function shouldEnableTimeoutLogs() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.enableTimeoutLogs') === 'true';
}

async function shouldEnableSeoPanelChanges() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.seoPanelChanges') === 'true';
}

async function shouldEnableMigration() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.migrateToV2') === 'true';
}

async function shouldEnableMigrationToV3() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.migrateToV3') === 'true';
}

async function shouldInstallMembersAreaV2() {
  const experimentsV2 = new Experiments({ scope: 'members-area-v2' });
  await experimentsV2.conduct('specs.membersAreaV2.BoBInstallation', 'false');
  return experimentsV2.get('specs.membersAreaV2.BoBInstallation') === 'true';
}

async function shouldInstallMembersAreaV3() {
  await waitForV3Conduction();
  return conductedV3Experiments?.get('specs.membersArea.SplitInstallation') === 'true';
}

async function shouldEnableV2SilentInstall() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.EnableV2SilentInstall') === 'true';
}

async function shouldRemoveUpdatingBoBControllerAndNavigation() {
  try {
    await waitForConduction();
  } catch (e) {}

  return conductedExperiments?.get('specs.responsive-editor.NoMeasureInstall') === 'true';
}

async function shouldEnableNotificationsIconFixer() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.NotificationsIconFixerOnV2') === 'true';
}

async function shouldAddNotificationsIcon() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.AddNotificationsIconOnV2') === 'true';
}

async function shouldEnableMenusDataFixer() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.EnableMenusDataFixer') === 'true';
}

async function shouldEnableV2TpaPageLinksDataFixer() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.EnableTpaPageLinksDataFixerForV2MenuItems') === 'true';
}

async function shouldEnableV3TpaPageLinksDataFixer() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.EnableTpaPageLinksDataFixerForV3MenuItems') === 'true';
}

async function shouldEnableV2PageInfoPanelCustomPage() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersAreaV2.EnablePageInfoPanelCustomPage') === 'true';
}

async function shouldShowOnboardingModal() {
  await waitForV3Conduction();
  return conductedV3Experiments?.get('specs.membersArea.ShowOnboardingModal') === 'true';
}

async function shouldEnableUnifiedManager() {
  await waitForV3Conduction();
  return conductedV3Experiments?.get('specs.membersArea.EnableUnifiedManager') === 'true';
}

async function shouldEnableUninstallMASubApps() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.UninstallMASubApps') === 'true';
}

async function shouldEnableExtendedUninstallMASubApps() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.ExtendedUninstallMASubApps') === 'true';
}

async function shouldEnableRouteCompression() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.AppDataRoutesCompressionMAV2') === 'true';
}

async function shouldAddManageMemberAccessAction() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.AddManageMemberAccessAction') === 'true';
}

// Called from the V2 (ma-on-msb) editor-ready path — only Studio/Responsive uses this path.
// Gates on: specs.ident.shouldInstallIdentityAuthAppV2 (Studio rollout flag)
// Editor3 also reports responsive, so explicitly exclude it — Editor3 has its own gate in V3.
async function shouldInstallIdentityAuthAppForMaV2() {
  if (getIsBuilderEditor() || !getIsResponsiveEditor()) {
    return false;
  }
  await waitForConduction();
  return conductedExperiments?.get('specs.ident.shouldInstallIdentityAuthAppV2') === 'true';
}

async function shouldUseApplyChangeToAllLanguagesForMaV2() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.UseApplyChangeToAllLanguagesForMaV2') === 'true';
}

// Called from the V3 (split-ma) editor-ready path — Classic, Studio, Editor3, and Studio2 all use this path.
// Each editor type has its own rollout flag for independent control:
//   Classic    → specs.ident.shouldInstallIdentityAuthAppV3
//   Studio     → specs.ident.shouldInstallIdentityAuthAppV2
//   Editor3    → specs.ident.shouldInstallIdentityAuthAppEditor3
//   Studio2    → specs.ident.shouldInstallIdentityAuthAppStudio2
async function shouldInstallIdentityAuthAppForMaV3() {
  if (getIsStudio2Editor()) {
    await waitForConduction();
    return conductedExperiments?.get('specs.ident.shouldInstallIdentityAuthAppStudio2') === 'true';
  }
  if (getIsBuilderEditor()) {
    await waitForConduction();
    return conductedExperiments?.get('specs.ident.shouldInstallIdentityAuthAppEditor3') === 'true';
  }
  if (getIsClassicEditor()) {
    await waitForConduction();
    return conductedExperiments?.get('specs.ident.shouldInstallIdentityAuthAppV3') === 'true';
  }
  if (getIsResponsiveEditor()) {
    await waitForConduction();
    return conductedExperiments?.get('specs.ident.shouldInstallIdentityAuthAppV2') === 'true';
  }
  return false;
}

// BuilderEditor
async function shouldInstallLatestBlocksVersion() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.shouldInstallLatestBlocksVersion') === 'true';
}

async function shouldAllowInstallingProfileBuilderEditor() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.AllowInstallingProfileE3') === 'true';
}

async function shouldEnableAPIRaceConditionHandling() {
  await waitForV3Conduction();
  return conductedV3Experiments?.get('specs.membersArea.APIRaceConditionHandling') === 'true';
}

async function shouldEnableDependencyInstallationCheck() {
  await waitForConduction();
  return conductedExperiments?.get('specs.membersArea.EnableDependencyInstallationCheck') === 'true';
}

async function shouldAddStandalonePagesRoutesToPublicAppData() {
  await waitForV3Conduction();
  return conductedV3Experiments?.get('specs.membersArea.addStandalonePageRoutesToPublicAppData') === 'true';
}

async function shouldUseScalableDimensionsForLoginBarOnBuilderEditor() {
  await waitForV3Conduction();
  return conductedV3Experiments?.get('specs.membersArea.useScalableDimensionsForLoginBarOnE3') === 'true';
}

async function shouldMigrateDisconnectedLoginBars() {
  await waitForConduction();
  return conductedExperiments?.get('migrateDisconnectedLoginBars') === 'B';
}

async function shouldUseLoginSocialBarV2() {
  await waitForV3Conduction();
  return conductedV3Experiments?.get('specs.members-area.LoginSocialBarV2') === 'true';
}

async function shouldEnableBuyAgainDefaultForNewSites() {
  await waitForV3Conduction();
  return conductedV3Experiments?.get('loginSocialBarBuyAgainDefaultForNewSites') === 'B';
}

export {
  initExperiments,
  initV3Experiments,
  shouldAddManageMemberAccessAction,
  shouldAddNotificationsIcon,
  shouldAddStandalonePagesRoutesToPublicAppData,
  shouldAllowInstallingProfileBuilderEditor,
  shouldEnableAPIRaceConditionHandling,
  shouldEnableBuyAgainDefaultForNewSites,
  shouldEnableDependencyInstallationCheck,
  shouldEnableExtendedUninstallMASubApps,
  shouldEnableMenusDataFixer,
  shouldEnableMigration,
  shouldEnableMigrationToV3,
  shouldEnableNotificationsIconFixer,
  shouldEnableRouteCompression,
  shouldEnableSeoPanelChanges,
  shouldEnableTimeoutLogs,
  shouldEnableUnifiedManager,
  shouldEnableUninstallMASubApps,
  shouldEnableV2PageInfoPanelCustomPage,
  shouldEnableV2SilentInstall,
  shouldEnableV2TpaPageLinksDataFixer,
  shouldEnableV3TpaPageLinksDataFixer,
  shouldInstallIdentityAuthAppForMaV2,
  shouldInstallIdentityAuthAppForMaV3,
  shouldInstallLatestBlocksVersion,
  shouldInstallMembersAreaV2,
  shouldInstallMembersAreaV3,
  shouldMigrateDisconnectedLoginBars,
  shouldRemoveUpdatingBoBControllerAndNavigation,
  shouldShowOnboardingModal,
  shouldUseApplyChangeToAllLanguagesForMaV2,
  shouldUseLoginSocialBarV2,
  shouldUseScalableDimensionsForLoginBarOnBuilderEditor,
};
